import { useEffect } from 'react';
import type { AccountStats } from '@/api/spec.ts';
import { amountToBig } from '@/utils/bigUtils';
import { useAccountStatsStore } from '../../features/portfolio/store/AccountStatsStore.ts';
import { telemetry } from '../../utils/telemetry';
import useStore from '../rootStore.ts';

/**
 * Reports lifetime trading history to the onboarding funnel from the exchange's own account stats,
 * which arrive inside the wallet snapshot on the first trading-socket message after auth.
 *
 * `trade_placed` can only ever say "traded while this tab was open" — it fires on message send, before
 * the engine acknowledges anything, and again on reposts and retries. `tt` is the wallet's real lifetime
 * trade count, so it is what decides whether someone belongs in the new-trader cohort at all.
 *
 * Re-arms itself when the stats store empties, which `resetUserStores()` does on logout, so a second
 * wallet in the same session reports its own history.
 */
export function useTraderProfileWatcher() {
  useEffect(() => {
    let reported = false;

    const report = (stats: AccountStats | undefined) => {
      if (!stats) {
        reported = false;
        return;
      }
      if (reported) return;
      // Volume needs the collateral scale. Missing means the exchange config has not landed yet, and
      // the next stats update retries — never divide by a guessed number of decimals.
      const decimals = useStore.getState().exchange?.token?.decimals;
      if (decimals == null) return;
      reported = true;
      telemetry.setTraderProfile({
        hasTraded: stats.tt > 0,
        totalTrades: stats.tt,
        lifetimeVolumeUsd: amountToBig(stats.tv, decimals).toNumber(),
      });
    };

    // The snapshot can land before this subscription exists (a remount, StrictMode's second pass), and
    // zustand does not replay current state to a new subscriber.
    report(useAccountStatsStore.getState().stats);
    return useAccountStatsStore.subscribe((state) => report(state.stats));
  }, []);
}
